"use client";

import { useAtom } from "jotai";
import { parseAsString, parseAsStringEnum, useQueryState } from "nuqs";
import { verificationTokenAtom } from "@/stores/authModalStore";
import { useRouter } from "@/i18n/navigation";

export type AuthStep =
  | "login"
  | "register"
  | "otp"
  | "create-password"
  | "forgot-password"
  | "reset-password";

export type AuthTab = "email" | "phone";
export type AuthMethod = "email" | "phone";

const queryParser = parseAsString.withOptions({
  history: "replace",
  clearOnDefault: true,
});

const authStepParser = parseAsStringEnum<AuthStep>([
  "login",
  "register",
  "otp",
  "create-password",
  "forgot-password",
  "reset-password",
]).withOptions({
  history: "replace",
  clearOnDefault: true,
});

const authMethodParser = parseAsStringEnum<AuthMethod>([
  "email",
  "phone",
]).withOptions({
  history: "replace",
  clearOnDefault: true,
});

/**
 * Clear all auth query states.
 * @param setAuth - Function to set auth state.
 * @param setTab - Function to set tab state.
 * @param setPhone - Function to set phone state.
 * @param setEmail - Function to set email state.
 * @param setAuthMethod - Function to set auth method state.
 */
const clearAuthQueryState = async ({
  setAuth,
  setTab,
  setPhone,
  setEmail,
  setAuthMethod,
  setAttempt,
}: {
  setAuth: (value: AuthStep | null) => Promise<unknown>;
  setTab: (value: string | null) => Promise<unknown>;
  setPhone: (value: string | null) => Promise<unknown>;
  setEmail: (value: string | null) => Promise<unknown>;
  setAuthMethod: (value: AuthMethod | null) => Promise<unknown>;
  setAttempt: (value: string | null) => Promise<unknown>;
}) => {
  await setAuth(null);
  await setTab(null);
  await setPhone(null);
  await setEmail(null);
  await setAuthMethod(null);
  await setAttempt(null);
};

/**
 * Hook to handle auth modal state.
 */
export const useAuthModal = () => {
  const router = useRouter();
  const [auth, setAuth] = useQueryState("auth", authStepParser);
  const [, setTab] = useQueryState("tab", queryParser);
  const [phone, setPhone] = useQueryState("phone", queryParser);
  const [email, setEmail] = useQueryState("email", queryParser);
  const [authMethod, setAuthMethod] = useQueryState("method", authMethodParser);
  const [attempt, setAttempt] = useQueryState("attempt", queryParser);
  const [verificationToken, setVerificationTokenState] = useAtom(
    verificationTokenAtom,
  );

  const step = auth ?? null;
  const isOpen = step !== null;

  const setVerificationToken = (nextToken: string | null) => {
    setVerificationTokenState(nextToken);
  };

  const openAuth = async (nextStep: AuthStep = "login", nextTab?: AuthTab) => {
    if (nextTab) {
      await setTab(nextTab);
    }
    await setAuth(nextStep);
  };

  const openLogin = async (nextTab: AuthTab = "email") => {
    setVerificationToken(null);
    await openAuth("login", nextTab);
  };

  const openPhoneLogin = async (nextPhone?: string | null) => {
    if (typeof nextPhone === "string") {
      await setPhone(nextPhone);
    }
    await setAuthMethod("phone");
    await openLogin("phone");
  };

  const openEmailLogin = async (nextEmail?: string | null) => {
    if (typeof nextEmail === "string") {
      await setEmail(nextEmail);
    }
    await setAuthMethod("email");
    await openLogin("email");
  };

  const openRegister = async () => {
    setVerificationToken(null);
    await openAuth("register");
  };

  const setStep = async (nextStep: AuthStep) => {
    await setAuth(nextStep);
  };

  const closeAuth = async () => {
    await clearAuthQueryState({
      setAuth,
      setTab,
      setPhone,
      setEmail,
      setAuthMethod,
      setAttempt,
    });
    setVerificationToken(null);
  };

  const completeAuthSuccess = async () => {
    const nextAttempt = attempt;
    await closeAuth();

    if (nextAttempt) {
      router.push(nextAttempt);
    }
  };

  return {
    isOpen,
    step,
    phone,
    email,
    authMethod,
    attempt,
    verificationToken,
    openAuth,
    openLogin,
    openPhoneLogin,
    openEmailLogin,
    openRegister,
    setStep,
    setPhone,
    setEmail,
    setAttempt,
    setAuthMethod,
    setVerificationToken,
    closeAuth,
    completeAuthSuccess,
  };
};
